import { useEffect, useMemo, useState } from 'react';
import { Search, Plus, Trash2, Loader2, Power } from 'lucide-react';
import {
  listarClientes,
  criarCliente,
  atualizarCliente,
  removerCliente,
  type Cliente,
} from '@/features/clientes/api';
import KPITile from '@/features/dashboard/KPITile';
import { cn } from '@/lib/cn';

export default function ClientesPage() {
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [filtro, setFiltro] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  // form de novo cliente
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [endereco, setEndereco] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function recarregar() {
    try {
      setClientes(await listarClientes());
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    recarregar();
  }, []);

  async function adicionar() {
    if (!nome.trim() || !telefone.trim()) {
      setErro('Nome e telefone são obrigatórios.');
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      await criarCliente({ nome: nome.trim(), telefone: telefone.trim(), endereco: endereco.trim() || null });
      setNome('');
      setTelefone('');
      setEndereco('');
      await recarregar();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  }

  async function alternarAtivo(c: Cliente) {
    try {
      await atualizarCliente(c.id, { ativo: !c.ativo });
      await recarregar();
    } catch (e) {
      setErro((e as Error).message);
    }
  }

  async function excluir(c: Cliente) {
    if (!confirm(`Remover ${c.nome} da lista? Ele deixa de poder usar o bot.`)) return;
    try {
      await removerCliente(c.id);
      await recarregar();
    } catch (e) {
      setErro((e as Error).message);
    }
  }

  const visiveis = useMemo(() => {
    const t = filtro.trim().toLowerCase();
    if (!t) return clientes;
    return clientes.filter((c) => c.nome.toLowerCase().includes(t) || (c.telefone || '').includes(t));
  }, [clientes, filtro]);

  const ativos = clientes.filter((c) => c.ativo).length;

  return (
    <div className="flex flex-col gap-s7">
      <header className="flex items-end justify-between gap-s5 flex-wrap">
        <div>
          <h1 className="t-overline mb-s3">Clientes</h1>
          <p className="font-display italic text-[40px] leading-tight text-fg-1 tracking-[-0.02em]">
            Quem pode regar o jardim.
          </p>
          <p className="font-body text-[12.5px] text-fg-3 mt-s2 max-w-[520px]">
            Só os números nesta lista (ativos) conseguem acionar o atendimento no WhatsApp. Quem não está aqui é ignorado.
          </p>
        </div>
        <div className="w-[280px] relative">
          <Search size={14} strokeWidth={1.6} className="absolute left-s3 top-1/2 -translate-y-1/2 text-fg-3 pointer-events-none" />
          <input
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            placeholder="Buscar nome ou telefone…"
            className="w-full bg-input border border-[var(--border)] rounded-sm pl-s7 pr-s4 h-[40px] font-body text-[13px] text-fg-1 outline-none focus:border-moss-400"
          />
        </div>
      </header>

      <section className="grid grid-cols-2 gap-s5 max-w-[460px]">
        <KPITile label="Cadastrados" valor={clientes.length} />
        <KPITile label="Ativos" valor={ativos} hint={`de ${clientes.length}`} />
      </section>

      {/* novo cliente */}
      <section className="bg-panel border border-[var(--border)] rounded-md p-s6 flex flex-col gap-s4">
        <span className="t-overline">Cadastrar cliente</span>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_1fr_auto] gap-s3 items-end">
          <Campo label="Nome">
            <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome do cliente" className={inputCls} />
          </Campo>
          <Campo label="Telefone (WhatsApp)">
            <input value={telefone} onChange={(e) => setTelefone(e.target.value)} placeholder="62 99999-9999" className={inputCls} />
          </Campo>
          <Campo label="Endereço (opcional)">
            <input value={endereco} onChange={(e) => setEndereco(e.target.value)} placeholder="Rua…" className={inputCls} />
          </Campo>
          <button
            onClick={adicionar}
            disabled={salvando}
            className="btn-accent inline-flex items-center justify-center gap-s2 h-[40px] px-s5 rounded-sm border text-fg-1 text-[13px] font-semibold active:scale-[0.98] disabled:opacity-60"
          >
            {salvando ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} strokeWidth={1.8} />}
            Adicionar
          </button>
        </div>
        {erro && <span className="font-body text-[12px] text-petal-300">{erro}</span>}
      </section>

      {/* lista */}
      <section className="bg-panel border border-[var(--border)] rounded-md overflow-hidden">
        <div className="px-s5 py-s3 border-b border-[var(--border-subtle)] flex items-center justify-between">
          <span className="t-overline">{visiveis.length} cliente(s)</span>
          <span className="t-overline">Status</span>
        </div>
        <div className="max-h-[55vh] overflow-y-auto">
          {carregando ? (
            <p className="font-display italic text-[18px] text-fg-3 text-center py-s9">Carregando…</p>
          ) : visiveis.length === 0 ? (
            <p className="font-display italic text-[18px] text-fg-3 text-center py-s9">
              Nenhum cliente cadastrado ainda.
            </p>
          ) : (
            visiveis.map((c) => (
              <div
                key={c.id}
                className="px-s5 py-s3 border-b border-[var(--border-subtle)] last:border-0 flex items-center justify-between gap-s4"
              >
                <div className="min-w-0">
                  <div className="font-body font-semibold text-[13.5px] text-fg-1 truncate">{c.nome}</div>
                  <div className="font-mono text-[11px] text-fg-3">
                    {c.telefone}
                    {c.endereco ? ` · ${c.endereco}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-s3 shrink-0">
                  <button
                    onClick={() => alternarAtivo(c)}
                    title={c.ativo ? 'Ativo — clique para desativar' : 'Inativo — clique para ativar'}
                    className={cn(
                      'inline-flex items-center gap-s2 h-[30px] px-s3 rounded-sm border text-[11.5px] font-semibold transition-colors',
                      c.ativo
                        ? 'border-[var(--border-strong)] text-moss-300 bg-card'
                        : 'border-transparent text-fg-3 hover:bg-card',
                    )}
                  >
                    <Power size={13} strokeWidth={1.8} />
                    {c.ativo ? 'Ativo' : 'Inativo'}
                  </button>
                  <button
                    onClick={() => excluir(c)}
                    title="Remover"
                    aria-label="Remover"
                    className="inline-flex items-center justify-center h-[30px] w-[30px] rounded-sm border border-transparent text-fg-3 hover:text-petal-300 hover:bg-card transition-colors"
                  >
                    <Trash2 size={14} strokeWidth={1.6} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}

const inputCls =
  'w-full bg-input border border-[var(--border)] rounded-sm px-s4 h-[40px] font-body text-[13px] text-fg-1 outline-none focus:border-moss-400';

function Campo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-s2">
      <span className="font-body text-[11px] text-fg-3">{label}</span>
      {children}
    </label>
  );
}
